import { Component, TemplateRef, computed, input, output, signal, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { NavigationListComponent, NavigationItem } from '../../atoms/navigation-list/navigation-list.component';
import { ButtonComponent, DropdownItem } from '../../atoms/button/button.component';

@Component({
  selector: 'htx-header',
  imports: [
    CommonModule,
    LogoComponent,
    NavigationListComponent,
    ButtonComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {

  /**
   * Navigation items
   */
  navigationItems = input<NavigationItem[]>([]);

  /**
   * Template for extra space between navigation and auth buttons
   */
  extraSpaceTemplate = input<TemplateRef<void> | null>(null);

  /**
   * Whether the user is logged in
   */
  isLoggedIn = input<boolean>(false);


  /**
   * Whether to show the login button when the user is not logged in
   */
  isLoginButtonVisible = input<boolean>(true);

  /**
   * Whether to show the logout button when the user is logged in
   */
  isLogoutButtonVisible = input<boolean>(true);

  /**
   * Login button label
   */
  loginButtonLabel = input<string>('Login');

  /**
   * Logout button label
   */
  logoutButtonLabel = input<string>('Logout');

  /**
   * Mobile breakpoint in pixels (default: 1024)
   */
  mobileBreakpoint = input<number>(1024);

  /**
   * Available language codes to display in the switcher (e.g. ['de', 'en']).
   * Leave empty to hide the language switcher.
   */
  availableLangs = input<string[]>([]);

  /**
   * Currently active language code (e.g. 'de').
   */
  activeLang = input<string>('');

  /**
   * Event emitted when the user selects a language.
   */
  langChanged = output<string>();

  /**
   * Event emitted when logo is clicked
   */
  logoClicked = output<void>();

  /**
   * Event emitted when login button is clicked
   */
  loginClicked = output<void>();

  /**
   * Event emitted when logout button is clicked
   */
  logoutClicked = output<void>();

  /**
   * Event emitted when a navigation item is clicked
   */
  navigationItemClicked = output<NavigationItem>();

  /**
   * Signal to track current window width
   */
  windowWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);

  /**
   * Computed: Whether to show mobile menu based on window width and breakpoint
   */
  showMobileMenu = computed(() => this.windowWidth() <= this.mobileBreakpoint());

  /**
   * Computed: Mobile menu items converted from NavigationItem to DropdownItem
   */
  mobileMenuItems = computed(() => {
    return this.navigationItems().map(item => ({
      label: item.label,
      value: item,
      icon: item.icon ?? ''
    } as DropdownItem));
  });

  @HostListener('window:resize')
  onWindowResize(): void {
    this.windowWidth.set(window.innerWidth);
  }

  onMobileMenuItemSelected(item: DropdownItem): void {
    // item.value contains the NavigationItem
    this.navigationItemClicked.emit(item.value as NavigationItem);
  }
}
